import React, { useEffect, useState } from "react";

type BottomSheetProps = {
  showSheet: boolean;
  isAnimating: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

// Remember to add the useBottomSheet hook in your component
export default function BottomSheet({
  showSheet,
  isAnimating,
  onClose,
  children,
}: BottomSheetProps) {
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragCurrentY, setDragCurrentY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY === null) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - dragStartY;

    e.preventDefault();
    e.stopPropagation();

    if (deltaY > 0) {
      setDragCurrentY(deltaY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (dragCurrentY === null || dragStartY === null) {
      setIsDragging(false);
      setDragStartY(null);
      setDragCurrentY(null);
      return;
    }

    const threshold = 100;

    if (dragCurrentY > threshold) {
      onClose();
    }

    setIsDragging(false);
    setDragStartY(null);
    setDragCurrentY(null);
  };

  useEffect(() => {
    if (showSheet) {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollTop}px`;
      document.body.style.width = "100%";

      return () => {
        const scrollTop = Math.abs(parseInt(document.body.style.top || "0"));

        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";

        window.scrollTo(0, scrollTop);
      };
    }
  }, [showSheet]);

  function handleOverlayClick(e: React.MouseEvent) {
    e.stopPropagation();
    onClose();
  }

  function handleContentClick(e: React.MouseEvent) {
    e.stopPropagation();
  }

  if (!showSheet) return null;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50! z-40 transition-opacity duration-300 ${
          isAnimating ? "opacity-50" : "opacity-0"
        }`}
        onClick={handleOverlayClick}
      />
      <div
        className="bottom-sheet border-box rounded-b-none p-0! fixed bottom-0 left-0 right-0 bg-white z-50 touch-none transition-transform duration-300 ease-out"
        style={{
          transform:
            isDragging && dragCurrentY
              ? `translateY(${dragCurrentY}px)`
              : isAnimating
                ? "translateY(0)"
                : "translateY(100%)",
        }}
        onClick={handleContentClick}
      >
        <div
          className="flex justify-center items-center py-3 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: "none" }}
        >
          <div className="h-1 w-16 bg-gray-800 rounded" />
        </div>
        <div className="bottom-sheet__content overflow-auto max-h-[85vh]">
          {children}
        </div>
      </div>
    </>
  );
}
